import produce from 'immer';
import {v4 as uuidv4} from 'uuid';

import ModelTypeConstants from 'constants/ModelTypeConstants';
import {
  ProcessExecutionType,
  ProcessFlowchartNodeDetailsType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {readQuerySafe, writeQuery, writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {SchemaById} from 'lookup-config/LookupConfigModalConstants';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';
import {
  RouterNodeAdapter,
  StopNodeAdapter,
} from 'processes/graph-builder-processes/processes-plugin/LogicNodeAdapters';
import {getTaskDefinitionFieldClientIdFromSchemaField} from 'processes/graph-builder-processes/processes-plugin/TaskSchemaNodeAdapters';
import {
  ProcessFlowchartObjectType,
  ProcessFlowchartValidationError,
} from 'processes/graph-builder-processes/validation/ProcessFlowchartForValidationTypes';
import {
  ProcessGraphBuilderNode,
  ProcessGraphBuilderNodeType,
  ProcessGraphBuilderOutputNode,
  ProcessGraphBuilderRootNode,
  ProcessGraphBuilderTaskNode,
} from 'processes/graph-builder-processes/validation/ProcessGraphBuilderConstants';
import {SchemaInfo} from 'processes/modals/field-mapping/FieldMappingConstants';
import {isFlowchartTaskSchema} from 'processes/processes-platform/isFlowchartTaskSchema';
import {AllConfigurableTaskDefinitionsQuery} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/__generated/AllConfigurableTaskDefinitionsQuery';
import {generateTaskSchemaNodeIdentifier} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/flowchartIdentifierUtils';
import {
  FLOWCHART_OUTPUT_SECONDARY_INFO,
  FLOWCHART_TASK_SECONDARY_INFO,
  InteractiveTemplateFlowchart,
  ProcessFlowchartNodeType,
  ProcessTaskDefinitionVersionGraphQL,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorConstants';
import {convertTaskDefinitionVersionToSpec} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNetworkUtils';
import {
  FlowchartNode,
  RouterStaticNodeData,
  TaskSchemaStaticNodeData,
  EndStaticNodeData,
  TaskSchemaNode,
  RouterNode,
  EndNode,
  PlaceholderNode,
  PlaceholderStaticNodeData,
  isInteractiveTaskNode,
  isInteractiveRouterNode,
  isInteractiveEndNode,
  PlaceholderType,
  TaskDefinitionVersionSpecWithTaskSchema,
} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorNodeTypes';
import {FlowchartOptions} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/processFlowchartConfigEditorUtils';
import {ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY} from 'processes/settings/graph-builder-flowchart-config/horizontal-flowchart/useAllSchemasById';
import {SchemaField} from 'schemas/fields/SchemaFieldConstants';
import {
  BenchlingNodeV2Status,
  SectionRowStatus,
} from 'ui-library/reactflow/nodes/BenchlingNode/v2/BenchlingNodeV2Constants';

import {ProcessTaskSchemaGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaGraphQL';
import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {ProcessFlowchartEditorConfigurator} from '../ProcessFlowchartEditorConfigurator';

export enum NodeTransitionPosition {
  SOURCE = 'source',
  TARGET = 'target',
}

export interface NodePosition {
  x: number;
  y: number;
}

export interface NodeSidepanelBaseData {
  selectedNode: FlowchartNode;
  interactiveFlowchart: InteractiveTemplateFlowchart;
  allSchemasById: SchemaById;
  flowchartTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL;
  flowchartTaskDefinitionId: string;
  flowchartEditorConfigurator: ProcessFlowchartEditorConfigurator;
  refetchFlowchartConfig?: () => void;
}

export function createTaskNode(
  staticData: TaskSchemaStaticNodeData,
  position: NodePosition,
  options: FlowchartOptions
): TaskSchemaNode {
  const executionType =
    staticData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType;

  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
  const isDeletable = adapter.isDeletable();

  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    position,
    // TODO(BNCH-138583): Move isDeletable off of the node data
    deletable: isDeletable,
    data: {
      ...staticData,
      nodeDetailsSidepanelContent: adapter.nodeDetailsSidepanelContent,
      onNodeHeaderClick: options.onNodeHeaderClick,
      onFieldClick: options.onFieldClick,
      onUpdateNodeData: options.onUpdateNodeData,
      getAddNodeOptionsForRightHandle: options.getAddNodeOptionsForRightHandle,
    },
  };
}

export function createRouterNode(
  staticData: RouterStaticNodeData,
  position: NodePosition,
  options: FlowchartOptions
): RouterNode {
  const adapter = new RouterNodeAdapter();
  const isDeletable = adapter.isDeletable();

  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.ROUTER_NODE,
    position,
    deletable: isDeletable,
    data: {
      ...staticData,
      onNodeHeaderClick: options.onNodeHeaderClick,
      getAddNodeOptionsForRightHandle: options.getAddNodeOptionsForRightHandle,
    },
  };
}

export function createStopNode(
  staticData: EndStaticNodeData,
  position: NodePosition,
  options: FlowchartOptions
): EndNode {
  const adapter = new StopNodeAdapter();
  const isDeletable = adapter.isDeletable();

  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.STOP_NODE,
    position,
    deletable: isDeletable,
    data: {
      ...staticData,
      // stop nodes don't open sidepanels today
      onNodeHeaderClick: () => {},
    },
  };
}

export function createPlaceholderNode(
  staticData: PlaceholderStaticNodeData,
  position: NodePosition,
  options: FlowchartOptions
): PlaceholderNode {
  return {
    id: staticData.id,
    type: ProcessFlowchartNodeType.PLACEHOLDER_NODE,
    position,
    data: {
      ...staticData,
      onNodeHeaderClick: options.onNodeHeaderClick,
      getAddNodeOptionsForRightHandle: options.getAddNodeOptionsForRightHandle,
    },
  };
}

export const stubbableFunctions = {
  getIdForNewPlaceholderNode: (): string => uuidv4(),
};

export function createPlaceholderNodeForTaskSchemaCreate(
  nodeName: string,
  executionType: ProcessExecutionType,
  position: NodePosition,
  options: FlowchartOptions
): PlaceholderNode {
  const id = stubbableFunctions.getIdForNewPlaceholderNode();

  const staticData: PlaceholderStaticNodeData = {
    id,
    nodeType: ProcessFlowchartNodeType.PLACEHOLDER_NODE,
    nodeName,
    nodeIcon: 'plus',
    placeholderForNodeTypeData: {
      placeholderType: PlaceholderType.CREATE_NEW_TASK_SCHEMA,
      placeholderForNodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
      executionType,
    },
  };
  return createPlaceholderNode(staticData, position, options);
}

/**
 * Creates a mock ProcessTaskDefinitionVersionGraphQL structure since the adapter expects this format.
 * TODO(BNCH-127153): Once we decide what we are doing with ACM/PCM, we will likely either remove
 * the transition modal or convert it to use task definition versions entirely. That's a large body of work
 * that procedures doesn't need, so punting on this for now.
 */
export function temporarilyCreateMockTaskDefinitionVersion(
  taskSchema: ProcessTaskSchemaGraphQL
): ProcessTaskDefinitionVersionGraphQL {
  return {
    __typename: 'ProcessTaskDefinitionVersionNode' as const,
    id: '1',
    applicationData: null,
    processTaskSchema: taskSchema,
    processTaskDefinition: {
      __typename: 'ProcessTaskDefinitionNode',
      id: '1',
    },
    inputFieldDefinitions: taskSchema.fields.map((field: SchemaField) => ({
      __typename: 'ProcessTaskDefinitionVersionInputFieldDefinitionNode',
      fieldIdentifier: field.apiId,
      fieldClientIdentifier: getTaskDefinitionFieldClientIdFromSchemaField(field),
      fieldDefinition: {
        __typename: 'ProcessFieldDefinitionNode',
        name: field.name,
        type: field.fieldType,
      },
    })),

    outputFieldDefinitions:
      taskSchema.outputSchema?.fields.map((field: SchemaField) => ({
        __typename: 'ProcessTaskDefinitionVersionOutputFieldDefinitionNode',
        fieldIdentifier: field.apiId,
        fieldClientIdentifier: getTaskDefinitionFieldClientIdFromSchemaField(field),
        fieldDefinition: {
          __typename: 'ProcessFieldDefinitionNode',
          name: field.name,
          type: field.fieldType,
        },
      })) ?? null,
  };
}

/**
 * Generate data for node, using task schema node adapter to customize
 */
export function createStaticNodeData(
  schemaInfo: SchemaInfo,
  taskSchema: ProcessTaskSchemaGraphQL
): TaskSchemaStaticNodeData {
  const isFlowchartTask = isFlowchartTaskSchema({executionType: taskSchema.executionType});
  const isFlowchartOutputNode = isFlowchartTask && schemaInfo.itemType !== ModelTypeConstants.PROCESS_TASK;
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(taskSchema.executionType);
  const mockTaskDefinitionVersion = temporarilyCreateMockTaskDefinitionVersion(taskSchema);
  const mockNodeDetailsType =
    schemaInfo.itemType === ModelTypeConstants.PROCESS_OUTPUT
      ? ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS
      : ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS;
  const taskDefinitionVersionSpec = convertTaskDefinitionVersionToSpec(mockTaskDefinitionVersion, [
    taskSchema,
  ]);
  return {
    id: generateTaskSchemaNodeIdentifier(taskSchema),
    nodeType: ProcessFlowchartNodeType.TASK_SCHEMA_NODE,
    nodeDetailsType: mockNodeDetailsType,
    nodeName: adapter.getNodeName(taskDefinitionVersionSpec, isFlowchartOutputNode),
    nodeIcon: adapter.getNodeIcon(taskDefinitionVersionSpec, isFlowchartOutputNode) ?? undefined,
    /** Show secondary information to differentiate Flowchart Task/Output nodes from task schema nodes. */
    nodeSecondaryInfo: isFlowchartTask
      ? isFlowchartOutputNode
        ? FLOWCHART_OUTPUT_SECONDARY_INFO
        : FLOWCHART_TASK_SECONDARY_INFO
      : undefined,
    sections: adapter.createSections(taskDefinitionVersionSpec, mockNodeDetailsType),
    taskDefinitionVersionSpec,
    taskSchemaId: taskSchema.id,
  };
}

/**
 * Creates a legacy graphbuilder node from a flowchart node.
 *
 * This should primarily be used when rendering configuration components which were designed for the
 * "graphbuilder" flowchart UI. For example, the router configuration component was built for the graphbuilder
 * UI and so deals with the legacy node representation.
 *
 * @param node The flowchart node to create a legacy graphbuilder node from.
 * @returns The legacy graphbuilder node.
 */
export function createLegacyGraphbuilderNodeFromFlowchartNode(node: FlowchartNode): ProcessGraphBuilderNode {
  if (isInteractiveTaskNode(node)) {
    if (node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS) {
      const rootNode: ProcessGraphBuilderRootNode = {
        clientId: node.id,
        type: ProcessFlowchartNodeDetailsType.ROOT_NODE_DETAILS,
        taskSchemaId: node.data.taskSchemaId,
        nodeType: ProcessGraphBuilderNodeType.FLOWCHART_TASK,
        name: node.data.nodeName,
      };
      return rootNode;
    } else if (node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS) {
      const taskNode: ProcessGraphBuilderTaskNode = {
        clientId: node.id,
        type: ProcessFlowchartNodeDetailsType.TASK_NODE_DETAILS,
        taskSchemaId: node.data.taskSchemaId,
        nodeType: ProcessGraphBuilderNodeType.FLOWCHART_STEP,
        name: node.data.nodeName,
      };
      return taskNode;
    } else if (node.data.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS) {
      const outputNode: ProcessGraphBuilderOutputNode = {
        clientId: node.id,
        type: ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
        nodeType: ProcessGraphBuilderNodeType.FLOWCHART_OUTPUT,
        taskSchemaId: node.data.taskSchemaId,
        name: node.data.nodeName,
      };
      return outputNode;
    } else {
      throw new Error('Invalid node type');
    }
  } else if (isInteractiveRouterNode(node)) {
    return {
      clientId: node.id,
      type: ProcessFlowchartNodeDetailsType.ROUTER_NODE_DETAILS,
      nodeType: ProcessGraphBuilderNodeType.FLOWCHART_STEP,
      name: node.data.nodeName,
      routerConfig: node.data.routerConfig,
    };
  } else if (isInteractiveEndNode(node)) {
    return {
      clientId: node.id,
      type: ProcessFlowchartNodeDetailsType.END_NODE_DETAILS,
      nodeType: ProcessGraphBuilderNodeType.FLOWCHART_STEP,
      name: node.data.nodeName,
    };
  } else {
    throw new Error('Invalid node type');
  }
}

export function getNodeAndRowStatusAndDescription(
  validationErrors?: Array<ProcessFlowchartValidationError>,
  status?: BenchlingNodeV2Status
): {
  nodeStatus?: BenchlingNodeV2Status;
  nodeDescription?: string;
  rowStatusAndDescriptionById: Record<string, {status: SectionRowStatus; description: string}>;
} {
  const rowStatusAndDescriptionById: Record<string, {status: SectionRowStatus; description: string}> = {};
  let nodeStatus = status;
  let nodeDescription;
  if (validationErrors != null && validationErrors.length > 0) {
    const nodeErrors = validationErrors.filter(
      (error) => error.object?.type === ProcessFlowchartObjectType.NODE
    );
    if (nodeErrors.length > 0) {
      nodeStatus = 'error';
      nodeDescription = nodeErrors.map((error) => error.errorMessage).join('\n');
    }
    const rowErrors = validationErrors.filter(
      (error) => error.object?.type === ProcessFlowchartObjectType.ROW
    );
    if (rowErrors.length > 0) {
      rowErrors.forEach((error) => {
        if (error.object?.id != null) {
          rowStatusAndDescriptionById[error.object.id] = {
            status: 'error',
            description: error.errorMessage ?? '',
          };
        }
      });
    }
  }
  return {nodeStatus, nodeDescription, rowStatusAndDescriptionById};
}

export function getRowStatusAndDescription(
  validationErrors?: Array<ProcessFlowchartValidationError>,
  status?: BenchlingNodeV2Status
): {status?: BenchlingNodeV2Status; description?: string} {
  if (validationErrors != null && validationErrors.length > 0) {
    return {status: 'error', description: validationErrors.map((error) => error.errorMessage).join('\n')};
  }
  return {status, description: undefined};
}

/**
 * Updates the task schema in the task definition cache.
 *
 * Note - the task schema query cache is already being updated in updateProcessTaskSchema()
 */
function cacheUpdatedTaskSchemaInTaskDefinitionCache(updatedTaskSchema: ProcessTaskSchemaGraphQL): void {
  const taskDefinitionsResult = readQuerySafe<AllConfigurableTaskDefinitionsQuery>({
    query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY,
  });
  if (taskDefinitionsResult != null) {
    const newTaskDefinitions = produce(
      taskDefinitionsResult.processTaskDefinitionsSupportingPlatformFeature,
      (draft) => {
        for (const taskDefinition of draft) {
          const latestVersion = taskDefinition.latestVersion;
          if (
            latestVersion != null &&
            !isInaccessible(latestVersion.processTaskSchema) &&
            latestVersion.processTaskSchema.id === updatedTaskSchema.id
          ) {
            latestVersion.processTaskSchema = updatedTaskSchema;
          }
        }
        return draft;
      }
    );
    writeQuery(
      {query: ALL_CONFIGURABLE_TASK_DEFINITIONS_QUERY},
      {
        ...taskDefinitionsResult,
        processTaskDefinitionsSupportingPlatformFeature: newTaskDefinitions,
        __typename: 'Query',
      }
    );
    writeFragmentsToCache(getFragmentWrapper('ProcessTaskDefinition'), newTaskDefinitions);
  }
}

/**
 * Updates the task schema in the task definition cache and updates the node with the new task definition version spec.
 */
export function cacheUpdatedTaskSchemaAndUpdateNode(
  newTaskSchema: ProcessTaskSchemaGraphQL,
  selectedNode: TaskSchemaNode,
  updateNode: (
    updater: (
      currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
    ) => TaskDefinitionVersionSpecWithTaskSchema,
    isBlocking: boolean
  ) => void,
  newTaskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL | null
): void {
  cacheUpdatedTaskSchemaInTaskDefinitionCache(newTaskSchema);
  updateNode((currentFlowchart) => {
    const currentNodeSpec = currentFlowchart.nodes[selectedNode.id];
    if (!currentNodeSpec) {
      throw new Error(`Node ${selectedNode.id} not found in current flowchart`);
    }
    return produce(currentNodeSpec, (draft) => {
      draft.newProcessTaskDefinitionVersion.taskSchema = newTaskSchema;

      if (newTaskDefinitionVersion != null) {
        draft.clientId = newTaskDefinitionVersion.id;
        draft.currentVersionId = newTaskDefinitionVersion.id;
        draft.inputFieldDefinitions = newTaskDefinitionVersion.inputFieldDefinitions;
        draft.outputFieldDefinitions = newTaskDefinitionVersion.outputFieldDefinitions;
        draft.newProcessTaskDefinitionVersion.applicationData = newTaskDefinitionVersion.applicationData;
      }
    });
  }, false);
}
